import { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import { SmartTopicChips } from "./SmartTopicChips";
import { QuestionInput } from "./QuestionInput";
import { BackgroundRain } from "./BackgroundRain";
import { RESTRICTED_PATTERNS } from "./api";

const MAX_CATEGORIES = 3;

export const FuturisticWelcomeScreen = ({
  selectedCategories,
  onCategorySelect,
  onQuestionSubmit,
  isProcessing,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showTopics, setShowTopics] = useState(false);
  const [aiDetectedCategories, setAiDetectedCategories] = useState([]);
  const pendingAutocompleteRef = useRef(undefined);

  const highlightedCategories = aiDetectedCategories;

  const isRestrictedInput = useMemo(() => {
    return (
      inputValue.trim().length > 5 &&
      RESTRICTED_PATTERNS.some((pattern) => pattern.test(inputValue.trim()))
    );
  }, [inputValue]);

  const handleInputChange = (value) => {
    setInputValue(value);
    if (value.trim().length === 0) {
      setShowTopics(false);
      setAiDetectedCategories([]);
    }
  };

  const handleSuggestionSelected = (suggestion, data) => {
    setInputValue(suggestion);
    pendingAutocompleteRef.current = data;

    if (data && data.categories && data.categories.length > 0) {
      const cats = data.categories
        .slice(0, MAX_CATEGORIES)
        .map((c) => c.toLowerCase());
      setAiDetectedCategories(cats);
      cats.forEach((cat) => {
        if (!selectedCategories.includes(cat)) {
          onCategorySelect(cat);
        }
      });
      setTimeout(() => setShowTopics(true), 400);
    }
  };

  const handleSubmit = (question) => {
    if (selectedCategories.length === 0) {
      onCategorySelect("other");
    }
    onQuestionSubmit(question, pendingAutocompleteRef.current);
    pendingAutocompleteRef.current = undefined;
  };

  const handleCategorySelect = (category) => {
    onCategorySelect(category);
  };

  return (
    <div className="gm-welcome">
      <span className="bgshade"></span>
      <main className="gm-welcome-main">
        <div className="gm-welcome-heading-wrap">
          <h1 className="gm-welcome-heading">
            What's your{" "}
            <span
              className="gm-welcome-highlight"
              style={{
                color: "#E6FF00",
              }}
            >
              prediction?
              <span className="gm-welcome-zap">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="27"
                  viewBox="0 0 24 27"
                  fill="none"
                >
                  <path
                    d="M2.21245 15.5031C1.9838 15.5039 1.75961 15.4398 1.56594 15.3182C1.37227 15.1967 1.21707 15.0227 1.11837 14.8164C1.01967 14.6101 0.981516 14.3801 1.00835 14.153C1.03518 13.9259 1.1259 13.7111 1.26995 13.5336L13.2325 1.20856C13.3222 1.10499 13.4445 1.03499 13.5792 1.01007C13.714 0.985154 13.8532 1.00679 13.974 1.07142C14.0949 1.13606 14.1902 1.23986 14.2442 1.36578C14.2983 1.4917 14.308 1.63226 14.2716 1.7644L11.9516 9.03856C11.8832 9.22165 11.8602 9.41861 11.8847 9.61253C11.9091 9.80645 11.9802 9.99155 12.0919 10.152C12.2036 10.3124 12.3525 10.4433 12.5259 10.5335C12.6993 10.6237 12.892 10.6704 13.0875 10.6698H21.5458C21.7744 10.669 21.9986 10.7332 22.1923 10.8547C22.386 10.9763 22.5412 11.1503 22.6399 11.3566C22.7386 11.5628 22.7767 11.7929 22.7499 12.0199C22.7231 12.247 22.6323 12.4618 22.4883 12.6394L10.5258 24.9644C10.4361 25.068 10.3138 25.138 10.179 25.1629C10.0443 25.1878 9.90504 25.1662 9.7842 25.1015C9.66336 25.0369 9.56808 24.9331 9.51401 24.8072C9.45993 24.6813 9.45028 24.5407 9.48662 24.4086L11.8066 17.1344C11.875 16.9513 11.898 16.7544 11.8736 16.5604C11.8491 16.3665 11.778 16.1814 11.6663 16.021C11.5547 15.8606 11.4057 15.7297 11.2323 15.6395C11.0589 15.5493 10.8662 15.5025 10.6708 15.5031H2.21245Z"
                    stroke="#E6FF00"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </span>
          </h1>
          <p className="gm-welcome-subtitle">
            Type your question to create a prediction market
          </p>
        </div>

        <div className="gm-welcome-input-wrap">
          <div className="gm-welcome-input-glow-wrap">
            <div className="gm-welcome-input-inner">
              <QuestionInput
                onSubmit={handleSubmit}
                onChange={handleInputChange}
                onSuggestionSelect={handleSuggestionSelected}
                isProcessing={isProcessing}
                placeholder="Will Bitcoin hit $100K this year?"
              />
            </div>
          </div>
        </div>

        <AnimatePresence>
          {showTopics && (
            <motion.div
              initial={{ opacity: 0, y: 80 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 80 }}
              transition={{
                type: "spring",
                stiffness: 80,
                damping: 18,
                delay: 0.15,
              }}
              className="gm-welcome-topics"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="gm-welcome-topics-badge"
              >
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <Sparkles className="gm-icon-sm gm-text-primary" />
                </motion.div>
                <span className="gm-welcome-topics-label">
                  {selectedCategories.length > 0 ? (
                    <>
                    <span>
                      AI selected {selectedCategories.length} tag
                      {selectedCategories.length > 1 ? "s" : ""}{" "}
                      </span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="6"
                        height="6"
                        viewBox="0 0 6 6"
                        fill="none"
                      >
                        <circle cx="3" cy="3" r="3" fill="#E6FF00" />
                      </svg>{" "}
                                <span>
                      tap to adjust
                      </span>
                    </>
                  ) : (
                    "Detecting topics..."
                  )}
                </span>
              </motion.div>

              <SmartTopicChips
                selectedCategories={selectedCategories}
                highlightedCategories={highlightedCategories}
                onSelect={handleCategorySelect}
                animateFromBottom={true}
                maxSelectable={MAX_CATEGORIES}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};
